import { set as setProp } from 'dot-prop';
import { Design, NodeTypeName } from '@app/types';
import { refreshCanvasItem } from './refreshCanvasItem';
import { updateLastChange } from './update-last-change';

type SyncObject = {
  id: string;
  name?: string;
  props: {
    config: any;
    lastLocalChange?: number;
    nodeTypeName?: NodeTypeName;
    parentComponentID?: string;
  }
};

export function syncObjectToSerialized(
  serialized: Design,
  object: SyncObject,
  changes: [string, unknown][]
): void {
  if (!changes.length) {
    return;
  }

  const applyChanges = (
    item: SyncObject,
    {
      applyInstanceChanges,
      isNode = false
    }: {
      applyInstanceChanges: boolean;
      isNode?: boolean;
    }
  ): void => {
    // Stamp before dispatching any refresh events so they have the updated author/update time.
    updateLastChange(item.props);

    changes.forEach(([key, newValue]) => {
      const tailKey = key.replace(/.*\./, '');

      // Some properties should not sync between component & nodes.
      const isInstanceChange = (
        item.props.nodeTypeName === NodeTypeName.moduleReference
        && ['inputs', 'outputs'].includes(tailKey)
      );

      if (isInstanceChange && !applyInstanceChanges) {
        return;
      }

      setProp(item, key, newValue);
      if (isNode) {
        window.apm.postEventToAllViews({
          target: 'request-refresh',
          event: {
            id: item.id,
            type: 'node',
            upDatedField: tailKey,
            props: item.props,
            newValue: newValue
          }
        });
      }
    });
  };

  const { objects, modules } = serialized.components;
  const parentComponentID = object.props.parentComponentID ?? object.id;
  if (!parentComponentID) {
    return;
  }

  applyChanges(object, { applyInstanceChanges: true });
  const parentObject = objects.find((o) => o.id === parentComponentID);
  if (parentObject) {
    applyChanges(parentObject, {
      applyInstanceChanges: parentObject.id === object.id
    });
  }

  modules.forEach((mod) => {
    mod.versions[1].flow.nodes.forEach((node) => {
      if (node.props.parentComponentID !== parentComponentID) {
        return;
      }

      applyChanges(node, {
        applyInstanceChanges: node.id === object.id,
        isNode: true
      });
      refreshCanvasItem(node);
    });
  });
};
